import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Plane, RefreshCw } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

const AdminAirlineUpdate = () => {
  const [updating, setUpdating] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [summary, setSummary] = useState<any>(null);

  const handleUpdate = async () => {
    setUpdating(true);
    setLog(['Starting update...']);
    setSummary(null);

    try {
      const { data, error } = await supabase.functions.invoke('update-airline-data', {
        body: {}
      });

      if (error) throw error;

      if (data.success) {
        setLog(data.log);
        setSummary(data.summary);
        toast.success('Airline data updated successfully!');
      } else {
        throw new Error(data.error || 'Update failed');
      }
    } catch (error: any) {
      console.error('Update error:', error);
      toast.error(`Failed to update: ${error.message}`);
      setLog(prev => [...prev, `❌ Error: ${error.message}`]);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
            <Plane className="h-8 w-8" />
            Airline Data Update
          </h1>
          <p className="text-muted-foreground">
            Update baggage allowance data for all airlines
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Update Airline Baggage Policies</CardTitle>
            <CardDescription>
              This will update baggage allowance data for 70+ major international airlines
              with verified information from their official sources.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              onClick={handleUpdate} 
              disabled={updating}
              className="w-full"
              size="lg"
            >
              {updating ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Updating Airlines...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Update Airline Data
                </>
              )}
            </Button>

            {summary && (
              <div className="space-y-2 p-4 bg-secondary/50 rounded-lg">
                <h3 className="font-semibold">Update Summary</h3>
                <div className="space-y-1 text-sm">
                  <p>✅ Successfully updated: <strong>{summary.updatedCount}</strong> airlines</p>
                  <p>❌ Errors: <strong>{summary.errorCount}</strong></p>
                  <p>📦 Total processed: <strong>{summary.totalProcessed}</strong></p>
                </div>
              </div>
            )}

            {log.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold">Update Log</h3>
                <ScrollArea className="h-[400px] w-full rounded-md border p-4">
                  <div className="space-y-1 font-mono text-xs">
                    {log.map((line, index) => (
                      <div 
                        key={index}
                        className={
                          line.startsWith('✅') ? 'text-green-600' :
                          line.startsWith('❌') ? 'text-red-600' :
                          line.startsWith('🚀') || line.startsWith('📊') || line.startsWith('📈') ? 'text-blue-600 font-bold' :
                          'text-muted-foreground'
                        }
                      >
                        {line}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Airlines Included in Update (70+ Total)</CardTitle>
            <CardDescription>
              Major airlines worldwide will be updated with official data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Major International Carriers</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div>Emirates</div>
                  <div>Qatar Airways</div>
                  <div>Singapore Airlines</div>
                  <div>Etihad Airways</div>
                  <div>Cathay Pacific</div>
                  <div>Turkish Airlines</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">US Carriers</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div>American Airlines</div>
                  <div>Delta Air Lines</div>
                  <div>United Airlines</div>
                  <div>Southwest Airlines</div>
                  <div>JetBlue</div>
                  <div>Alaska Airlines</div>
                  <div>Spirit Airlines</div>
                  <div>Frontier Airlines</div>
                  <div>Allegiant Air</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">European Carriers</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div>British Airways</div>
                  <div>Lufthansa</div>
                  <div>Air France</div>
                  <div>KLM</div>
                  <div>Aegean Airlines</div>
                  <div>Ryanair</div>
                  <div>easyJet</div>
                  <div>Iberia</div>
                  <div>SAS</div>
                  <div>TAP Portugal</div>
                  <div>Austrian Airlines</div>
                  <div>Swiss Air</div>
                  <div>Brussels Airlines</div>
                  <div>LOT Polish</div>
                  <div>Norwegian Air</div>
                  <div>Wizz Air</div>
                  <div>Transavia</div>
                  <div>Volotea</div>
                  <div>Vueling</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Asian Carriers</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div>Japan Airlines</div>
                  <div>ANA</div>
                  <div>Korean Air</div>
                  <div>Thai Airways</div>
                  <div>Vietnam Airlines</div>
                  <div>China Eastern</div>
                  <div>Garuda Indonesia</div>
                  <div>Malaysia Airlines</div>
                  <div>IndiGo</div>
                  <div>Air India</div>
                  <div>AirAsia</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Americas (Latin/South)</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div>LATAM Airlines</div>
                  <div>Copa Airlines</div>
                  <div>Avianca</div>
                  <div>Volaris</div>
                  <div>VivaAerobus</div>
                  <div>GOL</div>
                  <div>Azul</div>
                  <div>Air Canada</div>
                  <div>WestJet</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Middle East & Africa</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div>Saudia</div>
                  <div>Oman Air</div>
                  <div>Royal Jordanian</div>
                  <div>EgyptAir</div>
                  <div>Royal Air Maroc</div>
                  <div>Ethiopian Airlines</div>
                  <div>Kenya Airways</div>
                  <div>South African Airways</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Oceania</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div>Qantas</div>
                  <div>Virgin Atlantic</div>
                  <div>Air New Zealand</div>
                  <div>Virgin Australia</div>
                  <div>Jetstar</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAirlineUpdate;
